<script setup>
/**
 * Putting one number on the Do not contact list by hand.
 *
 * IT DOES NOT REPAIR WHAT IS TYPED, AND THAT IS THE WHOLE DESIGN. The sidecar's
 * `normaliseE164` is the single authority on what a stored number looks like: it
 * translates Arabic-Indic digits, strips invisible code points and separators, and then
 * REFUSES anything it cannot make exact rather than guessing. A second, looser copy of
 * those rules in the browser is how the two halves start disagreeing about which numbers
 * exist — and a number stored in a shape the send path cannot match is not a smaller bug
 * than a rejected form, it is a number that is permanently invisible to the check this
 * whole page exists to run. So the raw string is posted exactly as typed and the server
 * decides.
 *
 * WHAT IS CHECKED HERE is only what can be said usefully without deciding anything: an
 * empty box, and a number with no `+`. Both are the same two checks the BFF makes before
 * it forwards (`routes/api/outbound.ts`), so the local message and the server's cannot
 * contradict each other — the local one just arrives without a round trip.
 *
 * THE BUSY STATE IS NOT DECORATION. A submit with nothing changing for the length of a
 * round trip is how this product once queued duplicate paid jobs (README §7); here a
 * double press would post the same number twice, which is harmless upstream — `suppress`
 * is idempotent — and confusing on screen, because the second answer is indistinguishable
 * from the first.
 */
import { computed, ref } from 'vue';

import Button from 'dashboard/components-next/button/Button.vue';
import Banner from 'dashboard/components-next/banner/Banner.vue';

import WasloInput from '../WasloInput.vue';
import { useWasloI18n } from '../../i18n.js';
import { wasloApi } from '../../api/client.js';

const props = defineProps({
  /** Chatwoot account id. Null (mounted outside a matched route) → nothing to post to. */
  accountId: { type: Number, default: null },
});

const emit = defineEmits(['added']);

const { t } = useWasloI18n();

const key = name => `WASLO.OUTBOUND.NUMBERS.ADD.${name}`;

const phone = ref('');
const isSaving = ref(false);
/** Pinned under the box: a shape problem the owner can fix by retyping. */
const fieldError = ref('');
/** Above the button: a failure that is not about what was typed. */
const failure = ref('');

const canSubmit = computed(
  () => phone.value.trim() !== '' && !isSaving.value && props.accountId !== null
);

const submit = async () => {
  if (!canSubmit.value) return;
  fieldError.value = '';
  failure.value = '';

  // Trimmed for the CHECK only. The value posted below is untouched — see the header.
  const typed = phone.value.trim();
  if (!typed.startsWith('+')) {
    fieldError.value = t(key('INVALID'));
    return;
  }

  isSaving.value = true;
  try {
    const row = await wasloApi.addOutboundNumber(props.accountId, phone.value);
    phone.value = '';
    // The page owns the confirmation and the reload: it is the thing that knows whether
    // this number was already on the list, and it is where the owner's eyes go next.
    emit('added', row);
  } catch (error) {
    // `fields` is non-empty exactly when the BFF said `validation_failed`, and null for
    // every other code — the invariant `WasloApiError` documents, so no guard beyond this.
    if (error?.fields?.phone) fieldError.value = error.fields.phone;
    else failure.value = t(key('FAILED'));
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <!-- A real `<form>`, so Enter in the box submits. The button is `type="submit"` for the
       same reason; `@submit.prevent` keeps the page from navigating. -->
  <form
    class="flex flex-col gap-3"
    data-waslo-numbers-add
    @submit.prevent="submit"
  >
    <Banner v-if="failure" color="ruby" data-waslo-numbers-add-failure>
      {{ failure }}
    </Banner>

    <!--
      STACKED, NOT A ROW WITH THE BUTTON BESIDE IT. An `<input>` next to a fixed control on
      one flex line is the shape this tree has collapsed to 23px three separate times
      (README §3), and the hint above the box plus a message below it means the two would
      never align at any width anyway.
    -->
    <WasloInput
      v-model="phone"
      type="tel"
      :label="t(key('LABEL'))"
      :hint="t(key('HINT'))"
      :placeholder="t(key('PLACEHOLDER'))"
      :message="fieldError"
      message-type="error"
      :disabled="isSaving"
      data-waslo-numbers-add-phone
    />

    <Button
      type="submit"
      :label="isSaving ? t(key('ADDING')) : t(key('ACTION'))"
      icon="i-lucide-plus"
      size="sm"
      class="self-start"
      :is-loading="isSaving"
      :disabled="!canSubmit"
      data-waslo-numbers-add-submit
    />
  </form>
</template>
